import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Users, Shield, Bot, DollarSign, Plus, Power, PowerOff,
  Search, AlertTriangle, Check, X, Loader2, CreditCard,
  ArrowDownToLine, Clock, CheckCircle, ExternalLink, RefreshCw,
  Globe, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// ── API bases ──────────────────────────────────────────────────────────────
// Commander's own backend
const API_URL = process.env.REACT_APP_BACKEND_URL;

// BigBull / gaurilabs.com backend
// Set REACT_APP_GAURILABS_BACKEND_URL in Vercel env vars:
//   https://gaurilabs-production.up.railway.app
// If both apps share the same backend, set it to the same value as REACT_APP_BACKEND_URL
const GL_URL = process.env.REACT_APP_GAURILABS_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

// Tier map: BigBull tier IDs → Commander tier keys
const TIER_MAP = { tier_6: 'HARMONY', tier_9: 'POWER', tier_3: 'STRATEGY' };
const TIER_PRICE = { tier_6: 149, tier_9: 499, tier_3: 49 };

const TIER_COLORS = {
  STRATEGY: 'bg-[#06b6d4]/10 text-[#06b6d4] border-[#06b6d4]/30',
  HARMONY:  'bg-[#f97316]/10 text-[#f97316] border-[#f97316]/30',
  POWER:    'bg-[#eab308]/10 text-[#eab308] border-[#eab308]/30',
};

const STATUS_COLORS = {
  ACTIVE:      'bg-green-500/15 text-green-400',
  SUSPENDED:   'bg-red-500/15 text-red-400',
  DEACTIVATED: 'bg-[hsl(220,16%,18%)] text-gray-500',
};

const DEFAULT_USER = {
  email: '', password: '', name: '',
  tier: 'STRATEGY',
  payment_tx_hash: '', payment_network: 'SOL', notes: ''
};

// Helper: get stored JWT for BigBull admin calls
function getToken() {
  return localStorage.getItem('gaurilabs_access_token') || '';
}

// Helper: call BigBull backend with JWT auth
function glAxios() {
  return axios.create({
    baseURL: GL_URL,
    headers: { Authorization: `Bearer ${getToken()}` },
    withCredentials: true,
  });
}

export default function AdminDashboard() {
  // ── Commander state ───────────────────────────────────────
  const [stats, setStats]               = useState(null);
  const [users, setUsers]               = useState([]);
  const [payments, setPayments]         = useState([]);
  const [withdrawals, setWithdrawals]   = useState([]);
  const [tiers, setTiers]               = useState({});
  const [loading, setLoading]           = useState(true);
  const [creating, setCreating]         = useState(false);
  const [searchQuery, setSearchQuery]   = useState('');
  const [newUser, setNewUser]           = useState(DEFAULT_USER);
  const [showCreateModal, setShowCreateModal]           = useState(false);
  const [showPaymentsModal, setShowPaymentsModal]       = useState(false);
  const [showWithdrawalsModal, setShowWithdrawalsModal] = useState(false);

  // ── GauriLabs signups state ───────────────────────────────
  const [activeTab, setActiveTab]           = useState('users');   // 'users' | 'signups'
  const [signups, setSignups]               = useState([]);
  const [signupsLoading, setSignupsLoading] = useState(false);
  const [signupSearch, setSignupSearch]     = useState('');
  const [approvingId, setApprovingId]       = useState(null);

  // ── Fetch Commander data ──────────────────────────────────
  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, usersRes, tiersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/admin/users`),
        axios.get(`${API_URL}/api/admin/license-tiers`),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || []);
      setTiers(tiersRes.data.tiers || {});
    } catch (err) {
      toast.error('Failed to load Commander dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 10000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // ── Fetch GauriLabs signups ───────────────────────────────
  const fetchSignups = useCallback(async () => {
    setSignupsLoading(true);
    try {
      const { data } = await glAxios().get('/api/admin/signups');
      setSignups(data.items || []);
    } catch (err) {
      toast.error('Could not load GauriLabs signups. Check REACT_APP_GAURILABS_BACKEND_URL in Vercel.');
    } finally {
      setSignupsLoading(false);
    }
  }, []);

  // Auto-fetch signups when tab is active
  useEffect(() => {
    if (activeTab === 'signups') fetchSignups();
  }, [activeTab, fetchSignups]);

  // ── Approve GauriLabs signup ──────────────────────────────
  // Step 1: Promote user on BigBull backend (role → "client")
  // Step 2: Auto-fill Commander create modal so admin can provision Commander account
  const handleApproveSignup = async (signup) => {
    setApprovingId(signup.id);
    try {
      // Promote on BigBull
      await glAxios().post(`/api/admin/users/${signup.id}/promote`, { role: 'client' });
      toast.success(`${signup.name || signup.email} approved on GauriLabs.com`);

      // Update local state immediately
      setSignups(prev => prev.map(u => u.id === signup.id ? { ...u, role: 'client' } : u));

      // Auto-fill Commander create modal with matching tier
      const commanderTier = TIER_MAP[signup.tier] || 'STRATEGY';
      setNewUser({
        email:            signup.email,
        name:             signup.name || '',
        password:         '',   // Admin sets password for Commander
        tier:             commanderTier,
        payment_tx_hash:  '',
        payment_network:  'SOL',
        notes:            `Approved from GauriLabs.com — ${new Date().toLocaleDateString()}`,
      });

      // Open Commander account creation modal
      setShowCreateModal(true);
      toast(`Now create their Commander account for ${commanderTier} tier`, { icon: '🎯' });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Approval failed';
      toast.error(msg);
    } finally {
      setApprovingId(null);
    }
  };

  // ── Commander user actions ────────────────────────────────
  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/users`, newUser);
      toast.success(`Commander account created for ${newUser.email} — ${newUser.tier} tier`);
      setShowCreateModal(false);
      setNewUser(DEFAULT_USER);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/api/admin/users/${userId}/status`, { status: newStatus });
      toast.success(`Account ${newStatus.toLowerCase()}`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status: newStatus } : u));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleKillSwitch = async (userId, userName) => {
    if (!window.confirm(`Kill all running bots for ${userName}?`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/killswitch/${userId}`);
      toast.success(data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kill switch failed');
    }
  };

  const fetchPayments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/payments`);
      setPayments(data.payments || []);
      setShowPaymentsModal(true);
    } catch { toast.error('Failed to load payments'); }
  };

  const fetchWithdrawals = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/withdrawals`);
      setWithdrawals(data.withdrawals || []);
      setShowWithdrawalsModal(true);
    } catch { toast.error('Failed to load withdrawals'); }
  };

  const handleProcessWithdrawal = async (wdId, status) => {
    try {
      await axios.patch(`${API_URL}/api/admin/withdrawals/${wdId}`, { status });
      toast.success(`Withdrawal ${status.toLowerCase()}`);
      fetchWithdrawals();
      fetchAll();
    } catch { toast.error('Failed to process withdrawal'); }
  };

  // ── Filtered lists ────────────────────────────────────────
  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingSignups = signups.filter(u => !u.role || u.role === 'user');
  const filteredSignups = signups.filter(u =>
    u.email?.toLowerCase().includes(signupSearch.toLowerCase()) ||
    (u.name || '').toLowerCase().includes(signupSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#E8520F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-white flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#E8520F]" /> Admin Control Panel
          </h1>
          <p className="text-[hsl(215,16%,56%)]">Manage sub-users, licenses, bot operations, and GauriLabs signups</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}
          className="bg-[#E8520F] hover:bg-[#C9440C] text-white" data-testid="create-user-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Sub-User
        </Button>
      </div>

      {/* ── Stats ───────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Users,           color: '#E8520F', label: 'Total Clients',       value: stats.total_users,        sub: `${stats.active_users} active`,   onClick: null },
            { icon: Bot,             color: '#00CED1', label: 'Total Bots',          value: stats.total_bots,          sub: `${stats.running_bots} running`,  onClick: null },
            { icon: DollarSign,      color: '#10B981', label: 'Revenue',             value: `$${stats.total_revenue?.toLocaleString()}`, sub: 'View Log', onClick: fetchPayments },
            { icon: ArrowDownToLine, color: '#F59E0B', label: 'Pending Withdrawals', value: stats.pending_withdrawals, sub: 'Process',  onClick: fetchWithdrawals },
          ].map(({ icon: Icon, color, label, value, sub, onClick }) => (
            <Card key={label} className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)]">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}18` }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div>
                  <p className="text-xs text-[hsl(215,16%,56%)]">{label}</p>
                  <p className="text-xl font-bold text-white">{value}</p>
                  {onClick
                    ? <button onClick={onClick} className="text-xs text-[#E8520F] hover:underline">{sub}</button>
                    : <p className="text-xs text-[#10B981]">{sub}</p>
                  }
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Tier pills ──────────────────────────────────────── */}
      {stats?.tier_counts && Object.keys(stats.tier_counts).length > 0 && (
        <div className="flex gap-3 flex-wrap">
          {Object.entries(stats.tier_counts).map(([tier, count]) => (
            <span key={tier} className={`px-3 py-1 rounded-full text-xs font-medium border ${TIER_COLORS[tier] || 'bg-[hsl(220,16%,18%)] text-gray-400 border-transparent'}`}>
              {tier}: {count} {count === 1 ? 'license' : 'licenses'}
            </span>
          ))}
          {/* Pending signups badge */}
          {pendingSignups.length > 0 && (
            <button onClick={() => setActiveTab('signups')}
              className="px-3 py-1 rounded-full text-xs font-medium border bg-amber-500/10 text-amber-400 border-amber-500/30 flex items-center gap-1.5 hover:bg-amber-500/15 transition-colors">
              <Clock className="w-3 h-3" />
              {pendingSignups.length} pending from GauriLabs.com
            </button>
          )}
        </div>
      )}

      {/* ── Tab switcher ────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b border-[hsl(220,16%,18%)]">
        {[
          { id: 'users',   icon: Users,  label: 'Commander Accounts', count: users.length      },
          { id: 'signups', icon: Globe,  label: 'GauriLabs Signups',  count: pendingSignups.length, badge: true },
        ].map(function (tab) {
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-[#E8520F] text-white'
                  : 'border-transparent text-[hsl(215,16%,56%)] hover:text-white'
              }`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.count > 0 && (
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-black ${
                  tab.badge && tab.count > 0
                    ? 'bg-amber-500/20 text-amber-400'
                    : 'bg-[hsl(220,16%,18%)] text-[hsl(215,16%,56%)]'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ══════════════════════════════════════════════════════
          TAB: COMMANDER ACCOUNTS (original functionality)
         ══════════════════════════════════════════════════════ */}
      {activeTab === 'users' && (
        <Card className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)]">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-['Outfit'] text-white">Sub-User Accounts</CardTitle>
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search email or name..." className="pl-10 border-[hsl(220,16%,18%)] h-8 text-sm" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredUsers.length > 0 ? (
              <div className="space-y-1">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs text-[hsl(215,16%,45%)] uppercase tracking-wide font-medium border-b border-[hsl(220,16%,18%)]">
                  <div className="col-span-3">User</div>
                  <div className="col-span-2">License</div>
                  <div className="col-span-1">Status</div>
                  <div className="col-span-1 text-center">Bots</div>
                  <div className="col-span-2">Expires</div>
                  <div className="col-span-3 text-right">Actions</div>
                </div>
                {filteredUsers.map(user => (
                  <div key={user.id} className="grid grid-cols-12 gap-2 items-center px-3 py-3 rounded-lg hover:bg-[hsl(220,16%,14%)] transition-colors">
                    <div className="col-span-3">
                      <p className="text-sm font-medium text-white truncate">{user.name}</p>
                      <p className="text-xs text-[hsl(215,16%,45%)] truncate">{user.email}</p>
                    </div>
                    <div className="col-span-2">
                      {user.license
                        ? <span className={`px-2 py-0.5 rounded text-xs font-medium border ${TIER_COLORS[user.license.tier] || 'bg-[hsl(220,16%,18%)] text-gray-400 border-transparent'}`}>{user.license.tier}</span>
                        : <span className="text-xs text-[hsl(215,16%,45%)]">No license</span>
                      }
                    </div>
                    <div className="col-span-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[user.status] || ''}`}>{user.status}</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-medium text-white">{user.running_bots}</span>
                      <span className="text-xs text-[hsl(215,16%,45%)]">/{user.license?.bot_limit || 0}</span>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-[hsl(215,16%,56%)]">
                        {user.license?.expires_at ? new Date(user.license.expires_at).toLocaleDateString() : '—'}
                      </p>
                    </div>
                    <div className="col-span-3 flex justify-end gap-1">
                      {user.status === 'ACTIVE'
                        ? <Button size="sm" variant="outline" onClick={() => handleStatusChange(user.id, 'SUSPENDED')}
                            className="text-xs h-7 border-red-500/30 text-red-400 hover:bg-red-500/10">
                            <PowerOff className="w-3 h-3 mr-1" /> Suspend
                          </Button>
                        : <Button size="sm" variant="outline" onClick={() => handleStatusChange(user.id, 'ACTIVE')}
                            className="text-xs h-7 border-green-500/30 text-green-400 hover:bg-green-500/10">
                            <Power className="w-3 h-3 mr-1" /> Activate
                          </Button>
                      }
                      {user.running_bots > 0 && (
                        <Button size="sm" variant="outline" onClick={() => handleKillSwitch(user.id, user.name)}
                          className="text-xs h-7 border-red-300 text-red-400 hover:bg-red-500/10 font-medium">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Kill Bots
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-[hsl(215,16%,56%)]">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">{searchQuery ? 'No users match your search' : 'No sub-users yet'}</p>
                <p className="text-xs mt-1">Click "Create Sub-User" or approve a GauriLabs signup</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ══════════════════════════════════════════════════════
          TAB: GAURILABS.COM SIGNUPS
         ══════════════════════════════════════════════════════ */}
      {activeTab === 'signups' && (
        <div className="space-y-4">

          {/* Info banner */}
          <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-500/20 bg-amber-500/5">
            <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-400">GauriLabs.com Payment Verification</p>
              <p className="text-xs text-[hsl(215,16%,56%)] mt-0.5">
                Users who registered on gaurilabs.com and completed payment via NowPayments appear here.
                Verify payment in your <a href="https://nowpayments.io" target="_blank" rel="noreferrer"
                  className="text-amber-400 underline">NowPayments dashboard</a> (check by user email),
                then click <strong className="text-white">Approve</strong> to activate their Commander access.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchSignups} disabled={signupsLoading}
              className="border-[hsl(220,16%,18%)] text-[hsl(215,16%,56%)] hover:text-white shrink-0">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${signupsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Search */}
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,16%,45%)]" />
            <Input value={signupSearch} onChange={e => setSignupSearch(e.target.value)}
              placeholder="Search name or email..." className="pl-10 border-[hsl(220,16%,18%)] h-8 text-sm" />
          </div>

          {/* Table */}
          <Card className="border-[hsl(220,16%,18%)] bg-[hsl(220,18%,11%)]">
            <CardContent className="p-0">
              {signupsLoading ? (
                <div className="flex items-center justify-center py-16 gap-3 text-[hsl(215,16%,56%)]">
                  <Loader2 className="w-5 h-5 animate-spin text-[#E8520F]" />
                  Loading signups from GauriLabs.com...
                </div>
              ) : filteredSignups.length === 0 ? (
                <div className="text-center py-16">
                  <Globe className="w-10 h-10 mx-auto mb-3 opacity-20 text-white" />
                  <p className="text-sm text-[hsl(215,16%,56%)]">No signups found</p>
                  <p className="text-xs text-[hsl(215,16%,45%)] mt-1">New registrations from gaurilabs.com appear here</p>
                </div>
              ) : (
                <div className="space-y-0 divide-y divide-[hsl(220,16%,18%)]">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-xs text-[hsl(215,16%,45%)] uppercase tracking-wide font-medium">
                    <div className="col-span-3">User</div>
                    <div className="col-span-2">Plan</div>
                    <div className="col-span-2">Amount</div>
                    <div className="col-span-2">Status</div>
                    <div className="col-span-2">Joined</div>
                    <div className="col-span-1 text-right">Action</div>
                  </div>

                  {filteredSignups.map(u => {
                    var isPending      = !u.role || u.role === 'user';
                    var commanderTier  = TIER_MAP[u.tier] || null;
                    var planPrice      = u.tier ? TIER_PRICE[u.tier] : null;
                    var isApproving    = approvingId === u.id;

                    return (
                      <div key={u.id}
                        className={`grid grid-cols-12 gap-2 items-center px-4 py-3 transition-colors ${
                          isPending ? 'hover:bg-amber-500/5' : 'hover:bg-[hsl(220,16%,14%)]'
                        }`}>

                        {/* User */}
                        <div className="col-span-3">
                          <p className="text-sm font-medium text-white truncate">{u.name || '—'}</p>
                          <p className="text-xs text-[hsl(215,16%,45%)] truncate">{u.email}</p>
                        </div>

                        {/* Plan */}
                        <div className="col-span-2">
                          {commanderTier ? (
                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${TIER_COLORS[commanderTier] || ''}`}>
                              {commanderTier}
                            </span>
                          ) : (
                            <span className="text-xs text-[hsl(215,16%,45%)]">—</span>
                          )}
                        </div>

                        {/* Amount */}
                        <div className="col-span-2">
                          {planPrice ? (
                            <span className={`text-sm font-bold ${isPending ? 'text-amber-400' : 'text-green-400'}`}>
                              ${planPrice}/mo
                            </span>
                          ) : (
                            <span className="text-xs text-[hsl(215,16%,45%)]">—</span>
                          )}
                        </div>

                        {/* Status */}
                        <div className="col-span-2">
                          {isPending ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-amber-500/10 text-amber-400">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-green-500/10 text-green-400">
                              <CheckCircle className="w-3 h-3" /> {u.role}
                            </span>
                          )}
                        </div>

                        {/* Joined */}
                        <div className="col-span-2">
                          <p className="text-xs text-[hsl(215,16%,56%)]">
                            {new Date(u.created_at).toLocaleDateString()}
                          </p>
                        </div>

                        {/* Action */}
                        <div className="col-span-1 flex justify-end">
                          {isPending ? (
                            <Button size="sm" onClick={() => handleApproveSignup(u)}
                              disabled={isApproving}
                              className="h-7 px-3 text-xs bg-[#E8520F] hover:bg-[#C9440C] text-white font-bold flex items-center gap-1">
                              {isApproving
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <><Check className="w-3 h-3" /> Approve</>
                              }
                            </Button>
                          ) : (
                            <span className="text-xs text-[hsl(215,16%,45%)] flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-400" /> Done
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pending count summary */}
          {pendingSignups.length > 0 && (
            <p className="text-xs text-[hsl(215,16%,45%)] text-right">
              {pendingSignups.length} pending &middot; {signups.length - pendingSignups.length} approved
            </p>
          )}
        </div>
      )}

      {/* ── Create Sub-User Modal (same as original + auto-filled from approve) ─ */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <Plus className="w-5 h-5 text-[#E8520F]" /> Create Commander Account
            </DialogTitle>
            <DialogDescription className="text-[hsl(215,16%,56%)]">
              {newUser.email
                ? `Provisioning Commander access for ${newUser.email}`
                : 'Provision a new client account with a license tier'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name</Label>
                <Input value={newUser.name} onChange={e => setNewUser(p => ({...p, name: e.target.value}))}
                  placeholder="Client name" className="border-[hsl(220,16%,18%)] h-9" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Email</Label>
                <Input value={newUser.email} onChange={e => setNewUser(p => ({...p, email: e.target.value}))}
                  placeholder="client@example.com" type="email" className="border-[hsl(220,16%,18%)] h-9" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Commander Password</Label>
              <Input value={newUser.password} onChange={e => setNewUser(p => ({...p, password: e.target.value}))}
                placeholder="Set a strong password for Commander" type="password" className="border-[hsl(220,16%,18%)] h-9" />
              <p className="text-xs text-[hsl(215,16%,45%)]">
                Share this password with the client for Commander login.
              </p>
            </div>

            {/* Tier */}
            <div className="space-y-2">
              <Label className="text-xs">License Tier</Label>
              {Object.keys(tiers).length === 0 ? (
                <p className="text-xs text-[hsl(215,16%,45%)]">Loading tiers...</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(tiers).map(([key, tier]) => (
                    <button key={key} onClick={() => setNewUser(p => ({...p, tier: key}))}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        newUser.tier === key
                          ? 'border-[#E8520F] bg-[#E8520F]/5 ring-1 ring-[#E8520F]'
                          : 'border-[hsl(220,16%,18%)] hover:border-[#E8520F]/40'
                      }`}>
                      <p className="text-sm font-semibold text-white">{tier.name}</p>
                      <p className="text-lg font-bold text-[#E8520F]">${tier.price_usdt}<span className="text-xs font-normal text-[hsl(215,16%,45%)]">/mo</span></p>
                      <p className="text-xs text-[hsl(215,16%,56%)]">{tier.bot_limit} bot{tier.bot_limit > 1 ? 's' : ''} · {tier.credits} credits</p>
                      <div className="mt-1 space-y-0.5">
                        <p className="text-[10px] text-[hsl(215,16%,56%)] flex items-center gap-1">
                          {tier.features?.volume_bot ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-red-400" />} Volume Bot
                        </p>
                        <p className="text-[10px] text-[hsl(215,16%,56%)] flex items-center gap-1">
                          {tier.features?.lure_bot ? <Check className="w-3 h-3 text-green-500" /> : <X className="w-3 h-3 text-red-400" />} Lure Bot
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Payment TX Hash</Label>
                <Input value={newUser.payment_tx_hash} onChange={e => setNewUser(p => ({...p, payment_tx_hash: e.target.value}))}
                  placeholder="Optional" className="border-[hsl(220,16%,18%)] h-9 text-xs font-mono" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Network</Label>
                <select value={newUser.payment_network} onChange={e => setNewUser(p => ({...p, payment_network: e.target.value}))}
                  className="w-full h-9 rounded-md border border-[hsl(220,16%,18%)] px-3 text-sm bg-[hsl(220,18%,11%)] text-white">
                  <option value="SOL">Solana</option>
                  <option value="ETH">Ethereum</option>
                  <option value="BSC">BNB Chain</option>
                  <option value="USDT_TRC20">USDT TRC20</option>
                </select>
              </div>
            </div>

            {newUser.notes && (
              <p className="text-xs text-[hsl(215,16%,45%)] px-3 py-2 rounded border border-[hsl(220,16%,18%)] font-mono">
                {newUser.notes}
              </p>
            )}

            <Button onClick={handleCreateUser}
              disabled={creating || !newUser.email || !newUser.password || !newUser.name}
              className="w-full bg-[#E8520F] hover:bg-[#C9440C] text-white">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Commander Account
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Payments Modal ───────────────────────────────────── */}
      <Dialog open={showPaymentsModal} onOpenChange={setShowPaymentsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <CreditCard className="w-5 h-5 text-[#10B981]" /> Payment Log
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {payments.length > 0 ? payments.map((p, i) => (
              <div key={i} className="p-3 rounded-lg border border-[hsl(220,16%,18%)] bg-[hsl(220,16%,14%)]">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-white">${p.amount_usdt?.toLocaleString()} USDT</p>
                    <p className="text-xs text-[hsl(215,16%,56%)]">{p.payment_network}</p>
                  </div>
                  <p className="text-xs text-[hsl(215,16%,45%)]">{new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                {p.usdt_tx_hash && <p className="text-xs font-mono text-[hsl(215,16%,56%)] mt-1 truncate">TX: {p.usdt_tx_hash}</p>}
              </div>
            )) : <p className="text-sm text-[hsl(215,16%,56%)] text-center py-8">No payments recorded</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Withdrawals Modal ────────────────────────────────── */}
      <Dialog open={showWithdrawalsModal} onOpenChange={setShowWithdrawalsModal}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white font-['Outfit']">
              <ArrowDownToLine className="w-5 h-5 text-[#F59E0B]" /> Withdrawal Requests
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 mt-2">
            {withdrawals.length > 0 ? withdrawals.map((w, i) => (
              <div key={i} className="p-3 rounded-lg border border-[hsl(220,16%,18%)]">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-white">${w.amount_usd?.toFixed(2)} USD</p>
                    <p className="text-xs text-[hsl(215,16%,56%)]">{w.user_email} — {w.network}</p>
                    <p className="text-xs font-mono text-[hsl(215,16%,45%)] truncate mt-0.5">{w.wallet_address}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    w.status === 'PENDING' ? 'bg-amber-500/10 text-amber-400' :
                    w.status === 'APPROVED' || w.status === 'COMPLETED' ? 'bg-green-500/15 text-green-400' :
                    'bg-red-500/10 text-red-400'
                  }`}>{w.status}</span>
                </div>
                {w.status === 'PENDING' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={() => handleProcessWithdrawal(w.withdrawal_id, 'APPROVED')}
                      className="bg-green-500 hover:bg-green-600 text-white text-xs h-7">
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleProcessWithdrawal(w.withdrawal_id, 'REJECTED')}
                      className="text-xs h-7 border-red-500/30 text-red-400">
                      <X className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            )) : <p className="text-sm text-[hsl(215,16%,56%)] text-center py-8">No withdrawal requests</p>}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
